/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `CertificationsPolicyFields` for the Caintra Connect frontend.
 * @module components/AppointmentRegistrationSections/Certifications/CertificationsPolicyFields
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import RegistrationSelectField from "../shared/RegistrationSelectField.jsx";

const CertificationsPolicyFields = ({
  company,
  t,
  styles,
  REPSEProviderOptions,
  averageDeliveryTimesOptions,
  acceptedPaymentMethodsOptions,
  civilLiabilityInsuranceOptions,
  IMMEXPermitOptions,
  esProveedorREPSE,
  tiemposPromedioEntrega,
  metodosDePagoAceptados,
  seguroDeResponsabilidadCivil,
  permisoIMMEX,
  onSelectFieldChange,
  setEsProveedorREPSE,
  setTiemposPromedioEntrega,
  setMetodosDePagoAceptados,
  setSeguroDeResponsabilidadCivil,
  setPermisoIMMEX,
  getFieldValidation,
  missingRequiredFields,
}) => (
  <>
    {company &&
      (Object.hasOwn(company, "esProveedorREPSE") ||
        Object.hasOwn(company, "tiemposPromedioEntrega") ||
        Object.hasOwn(company, "metodosDePagoAceptados")) && (
        <div className={styles.inputRow}>
          {Object.hasOwn(company, "esProveedorREPSE") && (
            <RegistrationSelectField
              fieldKey="esProveedorREPSE"
              label={t("components.certifications.REPSEProviderLabel")}
              placeholder={t(
                "components.certifications.REPSEProviderPlaceholder",
              )}
              options={REPSEProviderOptions}
              value={esProveedorREPSE}
              onChange={onSelectFieldChange(
                setEsProveedorREPSE,
                "esProveedorREPSE",
              )}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              styles={styles}
            />
          )}
          {Object.hasOwn(company, "tiemposPromedioEntrega") && (
            <RegistrationSelectField
              fieldKey="tiemposPromedioEntrega"
              label={t("components.certifications.averageDeliveryTimesLabel")}
              placeholder={t(
                "components.certifications.averageDeliveryTimesPlaceholder",
              )}
              options={averageDeliveryTimesOptions}
              value={tiemposPromedioEntrega}
              onChange={onSelectFieldChange(
                setTiemposPromedioEntrega,
                "tiemposPromedioEntrega",
              )}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              styles={styles}
            />
          )}
          {Object.hasOwn(company, "metodosDePagoAceptados") && (
            <RegistrationSelectField
              fieldKey="metodosDePagoAceptados"
              label={t("components.certifications.acceptedPaymentMethodsLabel")}
              placeholder={t(
                "components.certifications.acceptedPaymentMethodsPlaceholder",
              )}
              options={acceptedPaymentMethodsOptions}
              value={metodosDePagoAceptados}
              onChange={onSelectFieldChange(
                setMetodosDePagoAceptados,
                "metodosDePagoAceptados",
              )}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              styles={styles}
            />
          )}
        </div>
      )}

    {company &&
      (Object.hasOwn(company, "seguroDeResponsabilidadCivil") ||
        Object.hasOwn(company, "permisoIMMEX")) && (
        <div className={`${styles.inputRow} ${styles.twoInputsRow}`}>
          {Object.hasOwn(company, "seguroDeResponsabilidadCivil") && (
            <RegistrationSelectField
              fieldKey="seguroDeResponsabilidadCivil"
              label={t("components.certifications.civilLiabilityInsuranceLabel")}
              placeholder={t(
                "components.certifications.civilLiabilityInsurancePlaceholder",
              )}
              options={civilLiabilityInsuranceOptions}
              value={seguroDeResponsabilidadCivil}
              onChange={onSelectFieldChange(
                setSeguroDeResponsabilidadCivil,
                "seguroDeResponsabilidadCivil",
              )}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              styles={styles}
            />
          )}
          {Object.hasOwn(company, "permisoIMMEX") && (
            <RegistrationSelectField
              fieldKey="permisoIMMEX"
              label={t("components.certifications.IMMEXPermitLabel")}
              placeholder={t("components.certifications.IMMEXPermitPlaceholder")}
              options={IMMEXPermitOptions}
              value={permisoIMMEX}
              onChange={onSelectFieldChange(
                setPermisoIMMEX,
                "permisoIMMEX",
              )}
              getFieldValidation={getFieldValidation}
              missingRequiredFields={missingRequiredFields}
              styles={styles}
            />
          )}
        </div>
      )}
  </>
);

export default CertificationsPolicyFields;
